import { useEffect, useState } from 'react'
import { BackHandler } from 'react-native'
import { useToastController } from '@my/ui'
import { AuthUser } from 'app/provider/auth'
import { GroupAnnouncementEditWizard } from './group-announcement-edit-wizard'
import { useUpdateAnnouncement } from '../commands'
import { GroupAnnouncement } from 'app/types'
import { concatDateAndTime } from '../helpers'
import { SubmitAnnouncementData } from './announcement-creation-view'
import { groupPostByIdQuery } from 'app/features/group/queries'

type GroupAnnouncementEditWizardProps = {
  open: boolean
  onOpenChange: (value) => void
  announcement: GroupAnnouncement
  user: Pick<AuthUser, 'id' | 'fullName' | 'avatar' | 'token'> | null
}

export const GroupAnnouncementEditWizardContainer = (props: GroupAnnouncementEditWizardProps) => {
  const toast = useToastController()
  const [submitting, setSubmitting] = useState(false)

  const [updateEvent] = useUpdateAnnouncement()

  useEffect(() => {
    const backAction = () => {
      props.onOpenChange(false);
      return props.open
    }

    const backHandler = BackHandler.addEventListener(
      "hardwareBackPress",
      backAction
    );

    return () => backHandler.remove();
  }, [])

  const handleSubmit = async (data: SubmitAnnouncementData) => {
    const { title, body, scheduledDate, scheduledTime } = data
    const scheduledDateTime =
      scheduledDate && scheduledTime
        ? concatDateAndTime(new Date(scheduledDate), scheduledTime)
        : null

    if (!props.user || !data.group.id) {
      toast.show("User or group doesn't exist", {
        burntOptions: {
          preset: 'error',
        },
      })
      return
    }

    setSubmitting(true)

    await updateEvent({
      variables: {
        groupPostId: props.announcement.id,
        title,
        body,
        ...(scheduledDateTime && { scheduledDate: scheduledDateTime.toISOString() }),
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: groupPostByIdQuery.query,
          variables: {
            groupPostId: props.announcement.id,
          },
        },
      ],
      onCompleted: async (data) => {
        if (!data?.updateAnnouncement?.success) {
          toast.show(data?.updateAnnouncement?.message || 'Failed to update announcement', {
            burntOptions: {
              preset: 'error',
            },
          })
          return
        }
        setSubmitting(false)
        props.onOpenChange(false)
        toast.show('Announcement updated')
      },

      onError: () => {
        toast.show('Failed to update announcement', {
          burntOptions: {
            preset: 'error',
          },
        })
        setSubmitting(false)
      },
    })
  }

  if (!props.user) return null

  return (
    <GroupAnnouncementEditWizard
      announcement={props.announcement}
      user={props.user}
      open={props.open}
      submitting={submitting}
      onOpenChange={props.onOpenChange}
      onSubmit={handleSubmit}
    />
  )
}
